import updateCabinetVisualizer from './updateCabinetVisualizer';
import { createDesignWithProject, saveCabinetDesign } from '../../../api/design';
import { setQueryParam } from '../../../utils/queryParams';
import { addDesignToProject } from '../../../utils/migrateLocalStorage';
import { useDrawerStore } from '../../ui';

export default async function createAndUpdateDesign(set, get, host, designName, extraData = {}, navigate) {
  try {
    const state = get().cabinetVisualizer;
    const userId = state.userId;
    const styleChoiceId = state.styleChoiceId;
    const currentProjectId = state.currentProjectId;
    const projects = state.projects || {};

    // Call v2 API with optional projectId and userId
    const result = await createDesignWithProject(host, 15, 'Cabinet', currentProjectId, userId);

    if (!result) {
      throw new Error('Failed to create design');
    }

    const { designId: newDesignId, projectId: resultProjectId } = result;

    const selectedCollection = useDrawerStore.getState().selectedCollection;
    const newDesignObject = {
      ProductTypeId: 15,
      DesignStyleChoiceid: styleChoiceId,
      CollectionId: selectedCollection?.itemId,
      projectId: resultProjectId, // Add project reference to design,
      userId, // Add userId to design object
      Created: new Date().toISOString(),
      Matrix: {
        Rows: 0,
        Columns: 0,
      },
      Products: [],
      Cart: [],
      // Intentional, extra data is overrides not baseline
      ...extraData,
      Id: newDesignId,
      DesignName: designName,
    };

    const response = await saveCabinetDesign(host, newDesignObject);

    // Update projects if we created a new one or need to add design to existing one
    let updatedProjects = { ...projects };

    if (!currentProjectId && resultProjectId) {
      // Create new project metadata
      updatedProjects[resultProjectId] = {
        id: resultProjectId,
        styleChoiceId: parseInt(styleChoiceId, 10),
        name: `${designName} Project`,
        created: new Date().toISOString(),
        lastModified: new Date().toISOString(),
        designIds: [newDesignId]
      };
    } else if (currentProjectId && resultProjectId) {
      // Add design to existing project
      const updatedState = addDesignToProject(
        { projects: updatedProjects, designs: state.designs },
        newDesignId,
        resultProjectId
      );
      updatedProjects = updatedState.projects;
    }

    const designIds = state.designIds;
    const styleDesignIds = state.styleDesignIds;

    const tempIdIndex = designIds.indexOf(extraData.Id);
    if (tempIdIndex !== -1) {
      const newDesignIds = [...designIds.filter((id) => id !== extraData.Id), newDesignId];
      const updatedStyleDesignIds = styleDesignIds.filter((id) => id !== extraData.Id);
      // Actually remove the temp design from the designs object
      const newDesigns = { ...state.designs, [newDesignId]: newDesignObject };
      if (extraData.Id) {
        delete newDesigns[extraData.Id];
      }

      if (parseInt(newDesignObject.DesignStyleChoiceid, 10) === parseInt(styleChoiceId, 10)) {
        updatedStyleDesignIds.push(newDesignId);
      }

      // Update URL BEFORE store to ensure that when the store update triggers a
      // re-render, the hydration useEffect reads the correct designId from the URL
      setQueryParam('designid', newDesignId);

      updateCabinetVisualizer(set, get, {
        selectedDesignId: newDesignId,
        designIds: newDesignIds, // Use the updated array with the correct order
        styleDesignIds: updatedStyleDesignIds, // Ensure proper order in wall tabs
        projects: updatedProjects, // Update projects
        currentProjectId: resultProjectId || currentProjectId, // Update current project
        designs: newDesigns,
      });
    } else {
      // Temporary ID NOT found, handle this situation

      // Add the new design ID anyway
      const updatedDesignIds = [...designIds, newDesignId]; // Append the new ID to the end

      // Add to styleDesignIds if the design matches current style choice - ALWAYS at the end
      let updatedStyleDesignIds = [...styleDesignIds];
      if (parseInt(newDesignObject.DesignStyleChoiceid, 10) === parseInt(styleChoiceId, 10)) {
        // Ensure the new design appears at the END of the wall tab list
        updatedStyleDesignIds.push(newDesignId);
      }

      // Update URL BEFORE store to ensure that when the store update triggers a
      // re-render, the hydration useEffect reads the correct designId from the URL
      setQueryParam('designid', newDesignId);

      updateCabinetVisualizer(set, get, {
        selectedDesignId: newDesignId,
        designIds: updatedDesignIds, // Use the updated array
        styleDesignIds: updatedStyleDesignIds, // Ensure new walls appear at the end
        projects: updatedProjects, // Update projects
        currentProjectId: resultProjectId || currentProjectId, // Update current project
        designs: {
          ...state.designs,
          [newDesignId]: newDesignObject,
        },
      });

    }

    // Return the newly created design ID for selection
    return newDesignId;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log(`Create Cabinet Design Error: ${err}`);
    return null;
  }
}
